import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/Common/Modal';
import { Button } from '@/components/Common/button';
import { MultiSelect, MultiSelectOption } from '@/components/Common/MultiSelect';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/Common/tabs';
import {
  chatbotsService,
  Chatbot,
  ChatbotCreate,
  ShortcutButton,
  ThemeDefinition,
} from '@/services/chatbots';
import {
  documentTemplateService,
  DocumentTemplate,
} from '@/services/documentTemplateService';
import { FolderSelectionModal } from './FolderSelectionModal';
import notify from '@/lib/notify';
import { Loader2, FolderOpen, ChevronRight, ChevronLeft, Plus, Trash2 } from 'lucide-react';

const MAX_SHORTCUT_BUTTONS = 8;

const DEFAULT_SYSTEM_PROMPT = `Vous êtes un assistant spécialisé. Répondez de manière claire et précise aux questions en vous basant sur les documents fournis.

**RÈGLES** :
- Répondez directement à la question
- Soyez concis et précis
- Citez vos sources avec [Passage X]
- Si l'information n'est pas disponible, indiquez-le clairement`;

interface ChatbotFormDialogProps {
  open: boolean;
  onClose: () => void;
  chatbot?: Chatbot | null;
  onSave: () => void;
  /** View-only mode: every field disabled, no save (org agents for non-managers). */
  readOnly?: boolean;
  /** Whether the current user may share an agent with the whole organization (org admin). */
  canShare?: boolean;
}

export const ChatbotFormDialog: React.FC<ChatbotFormDialogProps> = ({
  open,
  onClose,
  chatbot,
  onSave,
  readOnly = false,
  canShare = false,
}) => {
  const { t } = useTranslation();

  const retrievalOptions = [
    {
      key: 'is_out_of_scope_enabled' as const,
      label: t('chatbotForm.behavior.outOfScopeLabel', 'Bloquer les questions hors sujet'),
      description: t(
        'chatbotForm.behavior.outOfScopeDescription',
        "L'agent détecte les questions trop éloignées de son domaine et invite l'utilisateur à reformuler. Si vous désactivez cette option, il répondra plus librement, même sur des sujets moins liés."
      ),
    },
    {
      key: 'is_malicious_enabled' as const,
      label: t('chatbotForm.behavior.maliciousLabel', 'Bloquer les demandes suspectes'),
      description: t(
        'chatbotForm.behavior.maliciousDescription',
        "L'agent refuse certaines demandes risquées ou manifestement abusives. Si vous désactivez cette option, il sera moins protecteur face à ce type de requêtes."
      ),
    },
    {
      key: 'apply_source_type_and_theme_weighting_enabled' as const,
      label: t('chatbotForm.behavior.weightingLabel', 'Privilégier certaines sources et thématiques'),
      description: t(
        'chatbotForm.behavior.weightingDescription',
        "L'agent met davantage en avant les documents jugés les plus pertinents selon leur type et leur thème. Si vous désactivez cette option, la recherche restera plus neutre, mais parfois moins précise."
      ),
    },
  ];

  const [loading, setLoading] = useState(false);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [availableThemes, setAvailableThemes] = useState<ThemeDefinition[]>([]);
  const [themesLoading, setThemesLoading] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState<DocumentTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [formData, setFormData] = useState<ChatbotCreate>({
    name: '',
    description: '',
    system_prompt: DEFAULT_SYSTEM_PROMPT,
    theme_filters: [],
    folder_paths: [],
    is_out_of_scope_enabled: true,
    is_malicious_enabled: true,
    apply_source_type_and_theme_weighting_enabled: true,
    shortcut_buttons: [],
    allowed_template_ids: [],
  });

  const themeOptions: MultiSelectOption[] = availableThemes.map((theme) => ({
    value: theme.code,
    label: theme.label || theme.code,
    description: theme.description || undefined,
  }));

  const templateOptions: MultiSelectOption[] = availableTemplates.map((template) => ({
    value: template.id,
    label: template.is_active
      ? `${template.name} (${template.output_format})`
      : `${template.name} (${template.output_format}) — ${t('chatbotForm.inactiveSuffix', 'inactif')}`,
    description: template.description || undefined,
  }));

  const shortcutButtons = formData.shortcut_buttons || [];

  const loadThemeOptions = async () => {
    setThemesLoading(true);
    try {
      const response = await chatbotsService.getThemeOptions();
      setAvailableThemes(Array.isArray(response.valid_themes) ? response.valid_themes : []);
    } catch (error) {
      console.error('Error loading chatbot theme options:', error);
      // Defensive fallback expected by product rule.
      setAvailableThemes([]);
    } finally {
      setThemesLoading(false);
    }
  };

  const loadDocumentTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const templates = await documentTemplateService.list();
      setAvailableTemplates(templates);
    } catch (error) {
      console.error('Error loading document templates:', error);
      setAvailableTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadThemeOptions();
      loadDocumentTemplates();
      setActiveTab('info'); // Reset to first tab when opening
      if (chatbot) {
        setFormData({
          name: chatbot.name,
          description: chatbot.description || '',
          system_prompt: chatbot.system_prompt || '',
          visibility: chatbot.visibility || 'private',
          theme_filters: chatbot.theme_filters || [],
          folder_paths: chatbot.folder_paths || [],
          is_out_of_scope_enabled: chatbot.is_out_of_scope_enabled ?? true,
          is_malicious_enabled: chatbot.is_malicious_enabled ?? true,
          apply_source_type_and_theme_weighting_enabled:
            chatbot.apply_source_type_and_theme_weighting_enabled ?? true,
          shortcut_buttons: (chatbot.shortcut_buttons || []).map((button) => ({ ...button })),
          allowed_template_ids: chatbot.allowed_template_ids || [],
        });
      } else {
        setFormData({
          name: '',
          description: '',
          system_prompt: DEFAULT_SYSTEM_PROMPT,
          visibility: 'private',
          theme_filters: [],
          folder_paths: [],
          is_out_of_scope_enabled: true,
          is_malicious_enabled: true,
          apply_source_type_and_theme_weighting_enabled: true,
          shortcut_buttons: [],
          allowed_template_ids: [],
        });
      }
    }
  }, [open, chatbot]);

  const shortcutListRef = useRef<HTMLDivElement>(null);
  const scrollToNewShortcut = useRef(false);

  // The list scrolls on its own, so a newly added button would land out of view.
  useEffect(() => {
    if (!scrollToNewShortcut.current) return;
    scrollToNewShortcut.current = false;
    const list = shortcutListRef.current;
    if (list) list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
  }, [shortcutButtons.length]);

  const addShortcutButton = () => {
    scrollToNewShortcut.current = true;
    setFormData((prev) => {
      const current = prev.shortcut_buttons || [];
      if (current.length >= MAX_SHORTCUT_BUTTONS) return prev;
      return {
        ...prev,
        shortcut_buttons: [...current, { label: '', prompt: '', template_id: null }],
      };
    });
  };

  const updateShortcutButton = (index: number, changes: Partial<ShortcutButton>) => {
    setFormData((prev) => ({
      ...prev,
      shortcut_buttons: (prev.shortcut_buttons || []).map((button, i) =>
        i === index ? { ...button, ...changes } : button
      ),
    }));
  };

  const removeShortcutButton = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      shortcut_buttons: (prev.shortcut_buttons || []).filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    // Drop fully empty rows; a partially filled row is a user mistake.
    const filledButtons = (formData.shortcut_buttons || []).filter(
      (button) => button.label.trim() || button.prompt.trim()
    );
    if (filledButtons.some((button) => !button.label.trim() || !button.prompt.trim())) {
      notify.error(
        t('chatbotForm.shortcutIncomplete', 'Chaque bouton doit avoir un libellé et un prompt.')
      );
      setActiveTab('actions');
      return;
    }

    setLoading(true);

    try {
      const payload: ChatbotCreate = {
        ...formData,
        shortcut_buttons:
          filledButtons.length > 0
            ? filledButtons.map((button) => ({
                label: button.label.trim(),
                prompt: button.prompt.trim(),
                template_id: button.template_id || null,
              }))
            : null,
        allowed_template_ids:
          formData.allowed_template_ids && formData.allowed_template_ids.length > 0
            ? formData.allowed_template_ids
            : null,
      };

      if (chatbot) {
        await chatbotsService.update(chatbot.id, payload);
        notify.success(t('chatbotForm.updateSuccess', 'Agent mis à jour avec succès'));
      } else {
        await chatbotsService.create(payload);
        notify.success(t('chatbotForm.createSuccess', 'Agent créé avec succès'));
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving chatbot:', error);
      notify.error(
        error instanceof Error ? error.message : t('chatbotForm.saveError', "Erreur lors de l'enregistrement")
      );
    } finally {
      setLoading(false);
    }
  };

  const folderCount = formData.folder_paths?.length ?? 0;

  const footer = (
    <div className="flex w-full justify-between items-center">
      <div className="flex gap-2">
        {activeTab !== 'info' && (
          <Button
            type="button"
            onClick={() => {
              if (activeTab === 'prompt') setActiveTab('info');
              else if (activeTab === 'behavior') setActiveTab('prompt');
              else if (activeTab === 'filters') setActiveTab('behavior');
              else if (activeTab === 'actions') setActiveTab('filters');
            }}
            variant="outline"
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            {t('common.previous', 'Précédent')}
          </Button>
        )}
      </div>

      <div className="flex gap-2">
        <Button type="button" onClick={onClose} variant="outline" disabled={loading}>
          {readOnly ? t('knowledgeGraph.close', 'Fermer') : t('common.cancel', 'Annuler')}
        </Button>

        {readOnly ? null : activeTab !== 'actions' ? (
          <Button
            type="button"
            onClick={() => {
              if (activeTab === 'info') {
                if (!formData.name.trim()) {
                  notify.error(t('chatbotForm.nameRequired', 'Le nom est requis'));
                  return;
                }
                setActiveTab('prompt');
              } else if (activeTab === 'prompt') {
                if (!formData.system_prompt?.trim()) {
                  notify.error(t('chatbotForm.promptRequired', 'Le prompt de personnalité est requis'));
                  return;
                }
                setActiveTab('behavior');
              } else if (activeTab === 'behavior') {
                setActiveTab('filters');
              } else if (activeTab === 'filters') {
                setActiveTab('actions');
              }
            }}
          >
            {t('common.next', 'Suivant')}
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        ) : (
          <Button
            type="button"
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {chatbot ? t('common.save', 'Enregistrer') : t('common.create', 'Créer')}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="max-w-2xl"
      title={
        readOnly
          ? t('chatbotForm.titleDetails', "Détails de l'agent")
          : chatbot
          ? t('chatbotForm.titleEdit', "Modifier l'agent")
          : t('chatbotForm.titleNew', 'Nouvel agent')
      }
      footer={footer}
      hideCloseButton
    >
      <div className="space-y-6">
        <p className="text-sm text-muted-foreground">
          {t(
            'chatbotForm.intro',
            'Configurez votre assistant IA avec une personnalité et un périmètre documentaire spécifiques.'
          )}
        </p>

        {readOnly && (
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            {t(
              'chatbotForm.readOnlyNotice',
              "Cet agent est partagé par l'organisation. Vous pouvez le consulter mais pas le modifier."
            )}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} defaultValue="info">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="info">{t('chatbotForm.tabInfo', 'Informations')}</TabsTrigger>
            <TabsTrigger value="prompt">{t('chatbotForm.tabPrompt', 'Personnalité')}</TabsTrigger>
            <TabsTrigger value="behavior">{t('chatbotForm.tabBehavior', 'Comportement')}</TabsTrigger>
            <TabsTrigger value="filters">{t('chatbotForm.tabFilters', 'Filtres')}</TabsTrigger>
            <TabsTrigger value="actions">{t('chatbotForm.tabActions', 'Actions')}</TabsTrigger>
          </TabsList>

          {/* A disabled fieldset natively disables every control inside (view-only mode). */}
          <fieldset disabled={readOnly} className="contents">
          <TabsContent value="info" className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium">
                {t('chatbotForm.nameLabel', 'Nom')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-md"
                placeholder={t('chatbotForm.namePlaceholder', 'Expert Déontologie')}
                required
                maxLength={255}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium">{t('chatbotForm.descriptionLabel', 'Description')}</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-md"
                placeholder={t(
                  'chatbotForm.descriptionPlaceholder',
                  'Assistant spécialisé dans les questions de déontologie...'
                )}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  'chatbotForm.descriptionHelp',
                  "Une description claire aidera les utilisateurs à comprendre l'expertise de cet agent."
                )}
              </p>
            </div>

            {canShare && (
              <label className="flex items-start gap-3 rounded-md border border-border/60 p-3 cursor-pointer hover:bg-muted/40">
                <input
                  type="checkbox"
                  checked={formData.visibility === 'organization'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      visibility: e.target.checked ? 'organization' : 'private',
                    })
                  }
                  className="mt-1 h-4 w-4 rounded border-border"
                />
                <div className="space-y-1">
                  <div className="text-sm font-medium">
                    {t('chatbotForm.shareLabel', "Partager avec l'organisation")}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'chatbotForm.shareHelp',
                      "Rend cet agent visible par tous les membres de l'organisation. Ils pourront l'utiliser mais seuls les administrateurs pourront le modifier."
                    )}
                  </p>
                </div>
              </label>
            )}
          </TabsContent>

          <TabsContent value="prompt" className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium">
                {t('chatbotForm.promptLabel', 'Prompt de personnalité')} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.system_prompt}
                onChange={(e) =>
                  setFormData({ ...formData, system_prompt: e.target.value })
                }
                className="w-full px-3 py-2 border border-border rounded-md font-mono text-sm"
                placeholder={t(
                  'chatbotForm.promptPlaceholder',
                  'Définissez le comportement et le ton de votre assistant...'
                )}
                rows={12}
                required
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  'chatbotForm.promptHelp',
                  "Ce prompt définira comment l'assistant répond aux questions. Soyez précis sur le ton, le style de réponse et les consignes à suivre."
                )}
              </p>
            </div>
          </TabsContent>

          <TabsContent value="behavior" className="space-y-4 mt-4">
            <div className="space-y-3">
              {retrievalOptions.map((option) => (
                <label
                  key={option.key}
                  className="flex items-start gap-3 rounded-md border border-border/60 p-3 cursor-pointer hover:bg-muted/40"
                >
                  <input
                    type="checkbox"
                    checked={Boolean(formData[option.key])}
                    onChange={(e) =>
                      setFormData({ ...formData, [option.key]: e.target.checked })
                    }
                    className="mt-1 h-4 w-4 rounded border-border"
                  />
                  <div className="space-y-1">
                    <div className="text-sm font-medium">{option.label}</div>
                    <p className="text-xs text-muted-foreground">{option.description}</p>
                  </div>
                </label>
              ))}
              <p className="text-xs text-muted-foreground">
                {t(
                  'chatbotForm.behavior.help',
                  "Ces options modifient la manière dont l'assistant filtre certaines questions et choisit les documents les plus pertinents."
                )}
              </p>
            </div>
          </TabsContent>

          <TabsContent value="filters" className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium">{t('chatbotForm.themesLabel', 'Thématiques')}</label>
              <MultiSelect
                options={themeOptions}
                selected={formData.theme_filters || []}
                onChange={(values) => setFormData({ ...formData, theme_filters: values })}
                placeholder={t('chatbotForm.themesPlaceholder', 'Sélectionner des thématiques...')}
              />
              <p className="text-xs text-muted-foreground">
                {themesLoading
                  ? t('chatbotForm.themesLoading', 'Chargement des thématiques...')
                  : themeOptions.length === 0
                  ? t(
                      'chatbotForm.themesEmpty',
                      'Aucune thématique disponible. Tous les thèmes seront utilisés par défaut.'
                    )
                  : t('chatbotForm.themesHelp', 'Laissez vide pour toutes les thématiques.')}
              </p>
              {!themesLoading && themeOptions.length > 0 ? (
                <p className="text-xs text-muted-foreground">
                  {t(
                    'chatbotForm.themesSource',
                    'Les intitulés et descriptions proviennent de la configuration ontologique (`themes.yml`).'
                  )}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium">{t('chatbotForm.foldersLabel', 'Dossiers documentaires')}</label>
              <Button
                type="button"
                onClick={() => setFolderModalOpen(true)}
                variant="outline"
                className="w-full justify-start"
              >
                <FolderOpen className="mr-2 h-4 w-4" />
                {folderCount > 0
                  ? t('chatbotForm.foldersSelected', { count: folderCount })
                  : t('chatbotForm.foldersPlaceholder', 'Sélectionner des dossiers...')}
              </Button>
              <p className="text-xs text-muted-foreground">
                {t(
                  'chatbotForm.foldersHelp',
                  "Laissez vide pour tous les documents. La sélection d'un dossier parent inclut automatiquement tous ses sous-dossiers."
                )}
              </p>
            </div>
          </TabsContent>

          <TabsContent value="actions" className="space-y-6 mt-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium">
                {t('chatbotForm.shortcutsTitle', "Boutons d'action")}
              </label>
              <p className="text-xs text-muted-foreground">
                {t(
                  'chatbotForm.shortcutsHelp',
                  "Raccourcis affichés au-dessus de la zone de saisie du chat. Sans modèle, le prompt est envoyé comme message ; avec un modèle, un document est généré à partir de la conversation."
                )}
              </p>

              <div
                ref={shortcutListRef}
                className="space-y-3 max-h-[min(20rem,30vh)] overflow-y-auto gf-scrollbar pr-1.5"
              >
                {shortcutButtons.map((button, index) => (
                  <div key={index} className="rounded-md border border-border/60 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={button.label}
                        onChange={(e) => updateShortcutButton(index, { label: e.target.value })}
                        className="flex-1 px-3 py-2 border border-border rounded-md text-sm"
                        placeholder={t('chatbotForm.shortcutLabelPlaceholder', 'Libellé du bouton')}
                        maxLength={80}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => removeShortcutButton(index)}
                        aria-label={t('chatbotForm.removeShortcut', 'Supprimer ce bouton')}
                        title={t('chatbotForm.removeShortcut', 'Supprimer ce bouton')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <textarea
                      value={button.prompt}
                      onChange={(e) => updateShortcutButton(index, { prompt: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-md text-sm"
                      placeholder={t(
                        'chatbotForm.shortcutPromptPlaceholder',
                        'Prompt envoyé (ou instructions de génération si un modèle est choisi)'
                      )}
                      rows={2}
                      maxLength={2000}
                    />
                    <select
                      value={button.template_id || ''}
                      onChange={(e) =>
                        updateShortcutButton(index, { template_id: e.target.value || null })
                      }
                      className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background"
                      aria-label={t('chatbotForm.shortcutTemplate', 'Modèle de document (optionnel)')}
                    >
                      <option value="">
                        {t('chatbotForm.shortcutNoTemplate', 'Aucun modèle — envoyer le prompt')}
                      </option>
                      {availableTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name} ({template.output_format})
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addShortcutButton}
                disabled={shortcutButtons.length >= MAX_SHORTCUT_BUTTONS}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('chatbotForm.addShortcut', 'Ajouter un bouton')}
              </Button>
              {shortcutButtons.length >= MAX_SHORTCUT_BUTTONS && (
                <p className="text-xs text-muted-foreground">
                  {t('chatbotForm.shortcutsLimit', 'Maximum 8 boutons.')}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium">
                {t('chatbotForm.templatesTitle', 'Modèles de documents')}
              </label>
              <MultiSelect
                options={templateOptions}
                selected={formData.allowed_template_ids || []}
                onChange={(values) => setFormData({ ...formData, allowed_template_ids: values })}
                placeholder={t('chatbotForm.templatesPlaceholder', 'Sélectionner des modèles...')}
              />
              <p className="text-xs text-muted-foreground">
                {templatesLoading
                  ? t('chatbotForm.templatesLoading', 'Chargement des modèles...')
                  : templateOptions.length === 0
                  ? t(
                      'chatbotForm.templatesEmpty',
                      "Aucun modèle de document configuré pour l'organisation."
                    )
                  : t(
                      'chatbotForm.templatesHelp',
                      "Laissez vide pour autoriser tous les modèles de l'organisation."
                    )}
              </p>
            </div>
          </TabsContent>
          </fieldset>
        </Tabs>
      </div>

      <FolderSelectionModal
        open={folderModalOpen}
        onClose={() => setFolderModalOpen(false)}
        selectedPaths={formData.folder_paths || []}
        onSave={(paths) => setFormData({ ...formData, folder_paths: paths })}
      />
    </Modal>
  );
};
